import React from 'react';
import { useLocation } from 'react-router-dom';
import { useGamification } from '../../context/GamificationContext';

/**
 * Calculate level from XP
 */
const calculateLevel = (xp) => {
  return Math.floor(xp / 100) + 1;
};

/**
 * Calculate progress to next level (0-100%)
 */
const getProgressToNextLevel = (xp) => {
  const currentLevel = calculateLevel(xp);
  const xpForCurrentLevel = (currentLevel - 1) * 100;
  const xpForNextLevel = currentLevel * 100;
  const progress = ((xp - xpForCurrentLevel) / (xpForNextLevel - xpForCurrentLevel)) * 100;
  return Math.min(100, Math.max(0, progress));
};

// Fallback if GamificationContext is not available
const useGamificationSafe = () => {
  try {
    return useGamification();
  } catch {
    return { stats: { currentXP: 0, stats: { currentStreak: 0 } } };
  }
};

const QuizBadges = ({ quizUser }) => {
  const location = useLocation();
  const { stats: gamificationStats } = useGamificationSafe();
  
  // Use quiz user data if available, otherwise use gamification stats
  const userXP = quizUser?.xp || gamificationStats?.currentXP || 0;
  const userStreak = quizUser?.streak || gamificationStats?.stats?.currentStreak || 0;
  const level = calculateLevel(userXP);
  const progress = getProgressToNextLevel(userXP);
  
  // Only show on quiz pages
  if (!location.pathname.startsWith('/pharma-quiz') && !location.pathname.startsWith('/mothai-quiz')) {
    return null;
  }

  const CSS_VARS = {
    gold: '#D4AF37',
    goldLight: '#F4E4BA',
    goldDark: '#996515',
    greenDeep: '#1A3C34',
    greenForest: '#2D5A4A',
    greenHerb: '#4A7C59',
    cream: '#FDF8F0',
    white: '#FFFFFF',
  };

  return (
    <div className="flex items-center space-x-2 flex-shrink-0">
      {/* Level Badge */}
      <div 
        className="px-3 py-1.5 rounded-full font-bold text-sm shadow-md flex items-center space-x-1.5"
        style={{
          background: `linear-gradient(135deg, ${CSS_VARS.gold} 0%, ${CSS_VARS.goldDark} 100%)`,
          color: CSS_VARS.white,
          fontFamily: "'Kanit', sans-serif"
        }}
        title={`Level ${level}`}
      >
        <span className="text-xs">⭐</span>
        <span>Lv.{level}</span>
      </div>

      {/* XP Badge */}
      <div 
        className="px-3 py-1.5 rounded-full font-semibold text-sm shadow-md flex items-center space-x-1.5 relative overflow-hidden"
        style={{
          background: CSS_VARS.greenDeep,
          color: CSS_VARS.white,
          fontFamily: "'Kanit', sans-serif"
        }}
        title={`${userXP} XP`}
      >
        <span className="text-xs">💎</span>
        <span className="hidden lg:inline">{userXP}</span>
        <span className="lg:hidden">{userXP > 999 ? `${(userXP/1000).toFixed(1)}k` : userXP}</span>
        <span className="text-xs">XP</span>
        {/* Progress indicator */}
        <div 
          className="absolute bottom-0 left-0 h-0.5 transition-all duration-300"
          style={{
            width: `${progress}%`,
            background: CSS_VARS.gold
          }}
        />
      </div>

      {/* Streak Badge */}
      {userStreak > 0 && (
        <div 
          className="px-3 py-1.5 rounded-full font-bold text-sm shadow-md flex items-center space-x-1.5 animate-pulse"
          style={{
            background: `linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)`,
            color: CSS_VARS.white,
            fontFamily: "'Kanit', sans-serif"
          }}
          title={`Streak: ${userStreak} วัน`}
        >
          <span>🔥</span>
          <span className="hidden lg:inline">{userStreak}</span>
          <span className="lg:hidden">{userStreak}</span>
        </div>
      )}
    </div>
  );
};

export default QuizBadges;
